import React from 'react';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const TabActions = ({ card, updateCard }) => {
  const config = card.actions_config || {
    save_contact: true,
    exchange_contact: false,
  };

  // Auto-migrate legacy flat configuration into the new generic items array
  let items = config.items;
  if (!items) {
    items = [];
    if (config.book_meeting?.enabled || config.book_meeting?.url) {
      items.push({
        id: 'legacy-book',
        type: 'booking',
        label: config.book_meeting.label || 'Book a Meeting',
        url: config.book_meeting.url || '',
        is_visible: config.book_meeting.enabled
      });
    }
    if (config.custom_cta?.enabled || config.custom_cta?.url) {
      items.push({
        id: 'legacy-cta',
        type: 'custom',
        label: config.custom_cta.label || 'Visit Website',
        url: config.custom_cta.url || '',
        is_visible: config.custom_cta.enabled
      });
    }
  }

  const setConfig = (key, val) => updateCard('actions_config', { ...config, [key]: val });
  const updateItems = (newItems) => setConfig('items', newItems);

  const addAction = (type) => {
    const newItems = [...items, {
      id: Math.random().toString(36).substr(2, 9),
      type,
      label: type === 'booking' ? 'Book a Meeting' : 'Action',
      url: '',
      is_visible: true
    }];
    updateItems(newItems);
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...items];
    newItems[idx][field] = value;
    updateItems(newItems);
  };

  const removeItem = (idx) => {
    const newItems = items.filter((_, i) => i !== idx);
    updateItems(newItems);
  };

  const moveItem = (idx, direction) => {
    if (direction === -1 && idx === 0) return;
    if (direction === 1 && idx === items.length - 1) return;
    const newItems = [...items];
    const temp = newItems[idx];
    newItems[idx] = newItems[idx + direction];
    newItems[idx + direction] = temp;
    updateItems(newItems);
  };

  return (
    <div className="space-y-8">
      {/* Built-in Base Actions */}
      <div className="space-y-4">
        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Built-in Actions</h3>
        
        <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
          <label className="flex items-center justify-between cursor-pointer mb-1">
            <span className="font-bold text-slate-900">Save Contact</span>
            <input 
              type="checkbox" 
              checked={config.save_contact} 
              onChange={e => setConfig('save_contact', e.target.checked)}
              className="rounded border-slate-300 text-slate-900 focus:ring-slate-900 w-5 h-5"
            />
          </label>
          <p className="text-sm text-slate-500">Allows visitors to download a vCard directly to their phone.</p>
        </div>

        <div className="bg-slate-50 p-5 rounded-xl border border-slate-200">
          <label className="flex items-center justify-between cursor-pointer mb-1">
            <span className="font-bold text-slate-900">Share Your Contact</span>
            <input 
              type="checkbox" 
              checked={config.exchange_contact || false} 
              onChange={e => setConfig('exchange_contact', e.target.checked)}
              className="rounded border-slate-300 text-slate-900 focus:ring-slate-900 w-5 h-5"
            />
          </label>
          <p className="text-sm text-slate-500">Allows visitors to submit their details to your Meetoi connections.</p>
        </div>
      </div>

      {/* Engagement Framework Actions */}
      <div className="pt-6 border-t border-slate-200">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Custom Actions</h3>
            <p className="text-sm text-slate-500 mt-1">Add bookings or external links.</p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => addAction('booking')}>
            <SafeIcon icon={FiIcons.FiPlus} className="mr-2" /> Add Booking
          </Button>
        </div>

        <div className="space-y-5">
          {items.map((item, idx) => (
            <div key={item.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center text-slate-700 font-bold capitalize">
                  <SafeIcon icon={item.type === 'booking' ? FiIcons.FiCalendar : FiIcons.FiLink} className="mr-2 text-slate-400" />
                  {item.type} Action
                </div>
                <div className="flex gap-2">
                  <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="p-1.5 bg-slate-50 rounded border text-slate-400 hover:text-slate-900 disabled:opacity-30">
                    <SafeIcon icon={FiIcons.FiArrowUp} />
                  </button>
                  <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} className="p-1.5 bg-slate-50 rounded border text-slate-400 hover:text-slate-900 disabled:opacity-30">
                    <SafeIcon icon={FiIcons.FiArrowDown} />
                  </button>
                  <button onClick={() => removeItem(idx)} className="p-1.5 bg-red-50 rounded border border-red-100 text-red-500 hover:text-red-700 ml-2">
                    <SafeIcon icon={FiIcons.FiTrash2} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input 
                  label="Button Label" 
                  value={item.label} 
                  onChange={(e) => updateItem(idx, 'label', e.target.value)} 
                />
                <Input 
                  label="URL Destination" 
                  type="url"
                  value={item.url} 
                  onChange={(e) => updateItem(idx, 'url', e.target.value)} 
                  placeholder="https://..." 
                />
              </div>

              <label className="flex items-center space-x-2 cursor-pointer pt-3 border-t border-slate-100">
                <input 
                  type="checkbox" 
                  checked={item.is_visible} 
                  onChange={(e) => updateItem(idx, 'is_visible', e.target.checked)}
                  className="rounded border-slate-300 w-5 h-5 text-slate-900 focus:ring-slate-900"
                />
                <span className="text-sm font-medium text-slate-700">Enable this action</span>
              </label>
            </div>
          ))}

          {items.length === 0 && (
            <div className="text-center p-10 bg-slate-50 border border-slate-200 border-dashed rounded-xl">
              <p className="text-slate-500 text-sm">No custom actions added yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};